<template>
  <div :class="['app-notification', `app-notification--${variant}`]">
    <div class="app-notification__content">
      <h2 class="app-notification__title">
        <slot name="title"></slot>
      </h2>
      <p class="app-notification__message">
        <slot name="message"></slot>
      </p>
    </div>
    <div class="app-notification__action">
      <slot name="action">
        <div v-if="actions.length === 0" class="app-notification__close-btn">
          <a class="app-notification__close" @click="$emit('close')">
            <span class="app-notification__close-icon iconfont iclose"></span>
          </a>
        </div>

        <AppButton
          v-for="(action, index) in actions"
          :key="index"
          :dense="true"
          class="app-notification__action--button"
          @click="action.onClick()"
        >
          {{ action.label }}
        </AppButton>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  NotificationAction,
  NotificationVariant,
} from '@/services/notification.app'
import AppButton from '@/app/components/button/AppButton.vue'

/**
 * This is component is bare component for displaying notifications.
 * It defines specific styles for the different variants.
 *
 * @param variant (NotificationVariant) - Visual style variant:
 * 'unique' | 'success' | 'error' | 'warning' | 'info'. Default: 'unique'
 * @param actions (NotificationAction[]) - Array of action buttons with
 * `{ label: string, onClick: () => void }`. Default: []
 *   Note - if no actions are passed, the close button is displayed
 *        you can use the action slot to override all default action behavior
 * @event close - Emitted when the close button is clicked
 */
const { variant = 'unique', actions = [] } = defineProps<{
  variant?: NotificationVariant
  actions?: NotificationAction[]
}>()

defineEmits(['close'])
</script>

<style lang="scss" scoped>
.app-notification {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px 26px;
  position: relative;
  width: 100%;
  padding: 22px 32px;
  border-radius: 8px;
  box-shadow: $shadow;
  background: var(--notification-background);
  border: 1px solid var(--notification-border);
  color: var(--notification-color);

  @media only screen and (max-width: $w-tablet-s) {
    padding: 20px 40px;
  }

  @media only screen and (max-width: $w-mobile-s) {
    flex-direction: column;
    align-items: start;
    flex-wrap: wrap;
    padding: 20px 24px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: left;
  }

  &__close-btn {
    position: relative;

    @media only screen and (max-width: $w-mobile-s) {
      position: absolute;
      top: 15px;
      right: 17px;
      width: 12px;
    }
  }

  &__close {
    display: block;
    color: inherit;

    @include hover {
      color: inherit;
      transform: scale(1.2);
    }
  }

  &__title {
    font-size: 18px;
    line-height: 23px;
    @include font-um;

    @media only screen and (max-width: $w-mobile-s) {
      font-size: 16px;
    }
  }

  &__message {
    font-size: 14px;
    line-height: 21px;
    color: inherit;
  }

  &__action {
    display: flex;
    flex-wrap: wrap;
    gap: 5px 10px;
    flex-shrink: 0;
    margin-left: auto;

    &--button {
      padding-inline: 0;
      border: 1px solid var(--notification-border);
      background: transparent;
      color: var(--notification-color);

      @include hover {
        border-color: var(--notification-color);
      }

      &:last-of-type {
        border-color: transparent;
        background: var(--notification-color);
        color: $c-white;

        @include hover {
          background: var(--button-hover);
          border-color: transparent;
        }
      }
    }
  }

  &--unique {
    --notification-background: #{$c-yellow-2};
    --notification-color: #{$c-black-2};
    --notification-border: #{rgba($c-yellow, 0.3)};
    --button-hover: #{$c-black-8};
  }

  &--info {
    --notification-background: #{$c-blue-6};
    --notification-color: #{$c-blue-4};
    --notification-border: #{rgba($c-blue-4, 0.3)};
    --button-hover: #{$c-blue-2};
  }

  &--success {
    --notification-background: #{$c-green-3};
    --notification-color: #{$c-green-2};
    --notification-border: #{rgba($c-green-2, 0.3)};
    --button-hover: #{darken($c-green-2, 5)};
  }

  &--error,
  &--warning {
    --notification-background: #{$c-red-2};
    --notification-color: #{$c-red-6};
    --notification-border: #{rgba($c-red-6, 0.3)};
    --button-hover: #{$c-red};
  }
}
</style>
